import { useMemo, useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { CircularProgress, Stack, Typography } from '@mui/material';
import selectors from 'store/selectors';
import { setDialog } from 'store/actions/dialogActions';
import CustomDialog from 'components/CustomDialog/customDialog';
import CustomDialogHeader from 'components/CustomDialog/DialogHeader/dialogHeader.cmp';
import DIALOGS from 'utils/dialogIds';
import SearchInput from './search';
import { iconToComponentMapping } from 'pages/checklistV2/mappings';
import { getDelineatedChecklists } from 'api/checklistV2Api';
import CustomButton from 'components/Button/CustomButton';
import NoRecordsFound from 'components/ListView/EmptyRecords/noRecordsFound.cmp';
import CheckIcon from '@mui/icons-material/Check';
import EmptyState from './emptyState';

import {
  HeadingCnt,
  IconContainer,
  LoaderCnt,
  MainContainer,
  MainFooterCnt,
  TemplateIconAndNameCnt,
  TemplatesCnt,
} from './style';
import { useWorkspaceHook } from 'utils/CustomHooks/useWorkspaceHook';

const TemplatesListingAsRequestForm = () => {
  const dispatch = useDispatch();
  const dialogState = useSelector(selectors.getDialogState);
  const { open = false } =
    dialogState?.[DIALOGS?.TEMPLATES_LISTING_AS_REQUEST_FORM] || {};

  const [templatesList, setTemplatesList] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchValue, setSearchValue] = useState('');

  const [selected, setSelected] = useState<any>(null);
  const { navigateWithWorkspaceUrl } = useWorkspaceHook();

  const handleClose = useCallback(() => {
    dispatch(
      setDialog({
        open: false,
        dialogId: DIALOGS?.TEMPLATES_LISTING_AS_REQUEST_FORM,
      }),
    );
  }, [dispatch]);

  const getTemplatesList = async () => {
    const response = await getDelineatedChecklists({});

    if (response?.data) setTemplatesList(response?.data);
    setIsLoading(false);
  };

  useEffect(() => {
    getTemplatesList();
  }, [dispatch]);

  const dialogHeader = useMemo(
    () => (
      <CustomDialogHeader
        onClose={handleClose}
        other={{
          style: {
            alignItems: 'start',
          },
        }}
      >
        <HeadingCnt>
          <Typography className="title">Choose a Request Form</Typography>
        </HeadingCnt>
      </CustomDialogHeader>
    ),
    [handleClose],
  );

  const handleSelectedForm = () => {
    handleClose();
    navigateWithWorkspaceUrl(
      `/in-progress-checklist/${selected}?prevUrl=${`/requests`}&requestForm=true`,
    );
  };

  const dialogFooter = useMemo(
    () => (
      <MainFooterCnt>
        <Stack direction="row" spacing={2}>
          <CustomButton variant="text" onClick={handleClose}>
            Cancel
          </CustomButton>
          <CustomButton
            variant="contained"
            className="selectedBtn"
            onClick={handleSelectedForm}
            disabled={!selected}
          >
            Use Selected Form
          </CustomButton>
        </Stack>
      </MainFooterCnt>
    ),
    [handleClose, handleSelectedForm],
  );

  const onSearch = (value) => {
    setSearchValue(value);
  };

  const MuiIcon = ({ name, ...rest }) => {
    const IconComponent = iconToComponentMapping[name];
    return IconComponent ? <IconComponent {...rest} /> : null;
  };

  const ChecklistIcon = ({ icon, ...rest }) => {
    return (
      <IconContainer bgcolor={icon.color}>
        <MuiIcon name={icon.icon} {...rest} />
      </IconContainer>
    );
  };

  const filteredTemplates = useMemo(() => {
    if (!searchValue) return templatesList ?? [];
    return (
      templatesList?.filter((item) =>
        item?.name?.toLowerCase()?.includes(searchValue?.toLowerCase()),
      ) ?? []
    );
  }, [templatesList, searchValue]);

  const selectHandler = (id) => {
    setSelected(id);
  };

  return (
    <CustomDialog
      primaryBtnText="Create"
      hideActions={true}
      header={dialogHeader}
      footer={isLoading ? null : dialogFooter}
      dialogProps={{
        open,
        maxWidth: false,
        onClose: handleClose,
        sx: {
          '& .MuiDialog-paper': {
            maxWidth: 600,
          },
          '& .MuiDialogContent-root': {
            maxWidth: '100%',
            width: 600,
          },
        },
      }}
    >
      <MainContainer>
        {isLoading && (
          <LoaderCnt>
            <CircularProgress />
          </LoaderCnt>
        )}

        {!isLoading && <SearchInput onSearch={onSearch} />}

        {!isLoading && !searchValue && templatesList?.length === 0 && (
          <EmptyState />
        )}
        {!isLoading && searchValue && filteredTemplates?.length === 0 && (
          <Stack justifyContent="center" alignItems="center" height="400px">
            <NoRecordsFound />
          </Stack>
        )}
        {!isLoading && filteredTemplates?.length > 0 && (
          <TemplatesCnt>
            {filteredTemplates?.map((item) => (
              <TemplateIconAndNameCnt
                key={item?.id}
                className="template-name-cnt"
                onClick={() => selectHandler(item?.id)}
                sx={{
                  ...(selected === item?.id && {
                    backgroundColor: 'rgba(235, 234, 255, 1)',
                    border: '1px solid rgba(104, 104, 254, 1)',
                    borderRadius: '6px',
                  }),
                }}
              >
                <Stack direction="row" alignItems="center" gap={1}>
                  <ChecklistIcon icon={item?.icon} />
                  <span className="name">{item?.name}</span>
                </Stack>

                {selected === item?.id && <CheckIcon className="checkIcon" />}
              </TemplateIconAndNameCnt>
            ))}
          </TemplatesCnt>
        )}
      </MainContainer>
    </CustomDialog>
  );
};

export default TemplatesListingAsRequestForm;
